import './EditableUrlField.scss';

import { bem } from 'mission-control-common-components';
import React, { useCallback, useState } from 'react';

const [block, element] = bem('editable-url-field');

type EditableUrlFieldProps = {
  value: string;
  truncate?: boolean;
  placeholder?: string;
  onSave: (newValue: string) => void;
  testId?: string;
  error?: boolean;
  errorMessage?: string;
  readonly?: boolean;
};

const EditableUrlField = ({
  value,
  truncate = false,
  placeholder,
  onSave,
  testId = 'editable-url-field',
  error = false,
  errorMessage,
  readonly = false,
}: EditableUrlFieldProps): React.JSX.Element => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const textareaRefCallback = useCallback((node: HTMLTextAreaElement | null): void => {
    if (!node) return;

    node.focus();
    node.style.height = `${node.scrollHeight}px`;
  }, []);

  const startEditing = useCallback((): void => {
    setDraft(value);
    setIsEditing(true);
  }, [value]);

  const saveAndClose = useCallback((): void => {
    const trimmed = draft.trim();

    if (trimmed !== value) {
      onSave(trimmed);
    }
    setIsEditing(false);
  }, [draft, value, onSave]);

  const cancelEditing = useCallback((): void => {
    setDraft(value);
    setIsEditing(false);
  }, [value]);

  const onTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setDraft(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
  }, []);

  const onTextareaKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelEditing();
      }
    },
    [cancelEditing]
  );

  if (readonly) {
    return (
      <div className={block('readonly')} data-testid={testId}>
        <span className={element('value')}>{value}</span>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div
        className={block('editing')}
        data-testid={`${testId}-editing`}
        draggable
        onDragStart={(e): void => e.preventDefault()}
        onMouseDown={(e): void => e.stopPropagation()}
      >
        <textarea
          ref={textareaRefCallback}
          className={element('textarea')}
          value={draft}
          onChange={onTextareaChange}
          onBlur={saveAndClose}
          onKeyDown={onTextareaKeyDown}
          data-testid={`${testId}-textarea`}
        />
      </div>
    );
  }

  const displayText = value || placeholder;
  const valueModifiers = [truncate ? 'truncated' : '', !value ? 'placeholder' : ''].filter(Boolean).join(' ');
  const blockModifiers = [truncate ? 'truncated' : null, error ? 'error' : null].filter(Boolean);

  return (
    <div
      className={block(blockModifiers)}
      onClick={startEditing}
      onMouseDown={(e): void => e.stopPropagation()}
      data-testid={testId}
      role="button"
      tabIndex={0}
      onKeyDown={(e): void => {
        if (e.key === 'Enter' || e.key === ' ') startEditing();
      }}
    >
      <span className={element('value', valueModifiers)}>{displayText}</span>
      {error && errorMessage && (
        <span className={element('error-message')} data-testid={`${testId}-error`}>
          {errorMessage}
        </span>
      )}
    </div>
  );
};

export default EditableUrlField;
